import React, { useRef, useState } from 'react';
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native';
import { CameraView, CameraCapturedPicture } from 'expo-camera';
import { Icon, Modal, Spinner, useTheme } from '@ui-kitten/components';
import { IMessage, User } from 'react-native-gifted-chat';
import { v4 as uuidv4 } from 'uuid';
import { UIHelper as uh } from 'src/core';

interface ChatTakePictureProps {
  showCamera: boolean;
  setShowCamera: (showCamera: boolean) => void;
  sendMessage: (message: IMessage[]) => void;
  user: User;
}

const ChatTakePicture = ({ showCamera, setShowCamera, sendMessage, user }: ChatTakePictureProps) => {
  const [cameraType, setCameraType] = useState<'front' | 'back'>('back');
  const [picture, setPicture] = useState<CameraCapturedPicture>();
  const [loading, setIsLoading] = useState<boolean>(false);
  const cameraRef = useRef<any>(null); // workaround for missing CameraView types
  const th = useTheme();

  const styleContainer = StyleSheet.create({
    camera: { flex: 1, width: '100%', alignSelf: 'center' },
    cameraView: {
      flex: 1,
      width: '100%'
    },
    topButtons: {
      position: 'absolute',
      top: uh.h2DP(42)
    },
    flipButton: {
      left: uh.h2DP(24)
    },
    closeButton: {
      right: uh.h2DP(24)
    },
    previewButtons: {
      height: uh.h2DP(42),
      width: uh.h2DP(42),
      backgroundColor: 'white',
      borderRadius: uh.currentViewPort(),
      alignItems: 'center',
      justifyContent: 'center'
    },
    takePicture: {
      borderWidth: 1,
      borderColor: th['color-basic-100'],
      borderRadius: uh.currentViewPort(),
      position: 'absolute',
      bottom: uh.h2DP(48),
      height: uh.h2DP(64),
      width: uh.h2DP(64),
      alignSelf: 'center'
    },
    imagePreview: { height: '100%', width: '100%' },
    loading: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      height: '100%',
      zIndex: 999,
      top: 0,
      left: 0,
      width: uh.currentViewPort(),
      backgroundColor: 'rgba(0, 0, 0, 0.5)'
    }
  });

  const takePicture = async () => {
    if (!cameraRef.current?.takePictureAsync) return;
    setIsLoading(true);
    try {
      const takenPicture = await cameraRef.current.takePictureAsync();
      setPicture(takenPicture);
    } catch (error) {
      console.warn('Failed to take picture:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const submitPicture = () => {
    if (picture) {
      sendMessage([
        {
          _id: uuidv4(),
          text: '',
          image: picture.uri,
          createdAt: new Date(),
          user: user
        }
      ]);
      setShowCamera(false);
      setPicture(undefined);
    }
  };

  if (!showCamera) return null;

  return (
    <Modal visible={true} style={{ height: '100%', width: '100%' }}>
      {loading && (
        <View style={styleContainer.loading}>
          <Spinner size="giant" />
        </View>
      )}

      {picture ? (
        <View>
          <Image style={styleContainer.imagePreview} source={{ uri: picture.uri }} />
          <TouchableOpacity
            onPress={() => setPicture(undefined)}
            style={[styleContainer.topButtons, styleContainer.flipButton]}>
            <Icon height={uh.h2DP(24)} width={uh.h2DP(24)} fill={th['color-basic-100']} name="close-outline" />
          </TouchableOpacity>
          <TouchableOpacity onPress={submitPicture} style={[styleContainer.topButtons, styleContainer.closeButton]}>
            <Icon height={uh.h2DP(24)} width={uh.h2DP(24)} fill={th['color-primary-500']} name="paper-plane-outline" />
          </TouchableOpacity>
        </View>
      ) : (
        <CameraView ref={cameraRef} style={styleContainer.camera} facing={cameraType}>
          <View style={styleContainer.cameraView}>
            <TouchableOpacity
              onPress={() => setCameraType(cameraType === 'back' ? 'front' : 'back')}
              style={[styleContainer.topButtons, styleContainer.flipButton]}>
              <Icon height={uh.h2DP(24)} width={uh.h2DP(24)} fill={th['color-basic-100']} name="flip-2-outline" />
            </TouchableOpacity>
            <TouchableOpacity
              onPress={() => setShowCamera(false)}
              style={[styleContainer.topButtons, styleContainer.closeButton]}>
              <Icon height={uh.h2DP(24)} width={uh.h2DP(24)} fill={th['color-basic-100']} name="close-outline" />
            </TouchableOpacity>
            <TouchableOpacity onPress={takePicture} style={styleContainer.takePicture} />
          </View>
        </CameraView>
      )}
    </Modal>
  );
};

export default ChatTakePicture;
